import { IExitFormDetails } from 'Models'
import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer'
import { JSX } from 'react/jsx-runtime'

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  footer: {
    position: 'absolute',
    fontSize: 9,
    bottom: 15,
    left: 0,
    right: 0,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: 12,
    marginVertical: 6,
    fontWeight: 'bold',
  },
  table: {
    // display: 'table',
    width: 'auto',
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#ccc',
    marginBottom: 12,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCol: {
    flex: 1,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: '#ccc',
    padding: 4,
  },
  tableHeader: {
    fontWeight: 'bold',
    backgroundColor: '#f0f0f0',
  },
  question: {
    marginBottom: 4,
    fontSize: 9,
  },
  answer: {
    marginBottom: 8,
    fontSize: 9,
    fontStyle: 'italic',
  },
})


const ExitFormPdf = ({
  dataset,
}: {
  dataset: IExitFormDetails | IExitFormDetails[] | undefined
}) => {
  const items = Array.isArray(dataset) ? dataset : dataset ? [dataset] : []

  return (
    <Document
      title='Employee Exit Interview'
      author='TG - Management'
      subject='Exit Form'
      keywords='TG, hr, exit, interview'
      creator='TG EMP Portal'
      producer='TG - Management'
    >
      {items.length > 0 &&
        items?.map((exit: any, index: number) => (
          <Page size='A4' style={styles.page} key={index}>
            <View style={styles.header}>
              <View>
                <Text style={{ fontSize: 14, fontWeight: 'bold' }}>
                  {exit.employee.firstName} {exit.employee.lastName}
                </Text>
                <Text>{exit.employee.emailAddress}</Text>
                <Text>Employee ID: {exit.employee.employeeDisplayId}</Text>
                <Text>
                  Position: {exit.currentPosition} | Supervisor: {exit.supervisorName}
                </Text>
                <Text>
                  Start: {exit.employmentStartDate} | End: {exit.employmentEndDate}
                </Text>
              </View>
              {exit.employee?.photo?.url && (
                <Image
                  src={exit.employee.photo.url}
                  style={{ width: 60, height: 60, borderRadius: 30 }}
                />
              )}
            </View>

            <Text style={styles.sectionTitle}>Reasons for Leaving</Text>
            {exit.reasonsForLeaving.map((reason: any, index: any) => (
              <Text key={index} style={styles.answer}>
                - {reason}
              </Text>
            ))}

            <Text style={styles.sectionTitle}>Other Reasons for Leaving</Text>
            {exit && (
              <Text style={styles.answer}>
                Reason Explanation: {exit.otherReasonExplanation ?? 'not stated'}
              </Text>
            )}
            {exit && (
              <Text style={styles.answer}>
                Specific Reason for Resignation:{' '}
                {exit.specificEventPromptedResignation ?? 'not stated'}
              </Text>
            )}
            {exit && (
              <Text style={styles.answer}>
                Specific Reason for Explanation: {exit.specificEventExplanation ?? 'not stated'}
              </Text>
            )}
            {exit && (
              <Text style={styles.answer}>
                Reason Discussed with Supervisor: {exit.discussedWithSupervisor ? 'Yes' : 'No'}
              </Text>
            )}

            <Text style={styles.sectionTitle}>Items Returned</Text>
            {exit.returnOf.map((ctr: any, index: number) => (
              <Text key={index} style={styles.answer}>
                - {ctr}
              </Text>
            ))}

            <Text style={styles.sectionTitle}>Ratings</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                {[
                  'Training',
                  'Supervisor',
                  'Colleagues',
                  'Salary',
                  'Benefits',
                  'Workload',
                  'Overall',
                ].map((h) => (
                  <Text key={h} style={[styles.tableCol, styles.tableHeader]}>
                    {h}
                  </Text>
                ))}
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCol}>{exit.trainingRating}</Text>
                <Text style={styles.tableCol}>{exit.supervisorRelationshipRating}</Text>
                <Text style={styles.tableCol}>{exit.fellowEmployeesRelationshipRating}</Text>
                <Text style={styles.tableCol}>{exit.salaryRating}</Text>
                <Text style={styles.tableCol}>{exit.benefitsRating}</Text>
                <Text style={styles.tableCol}>{exit.workloadRating}</Text>
                <Text style={styles.tableCol}>{exit.overallSatisfactionRating}</Text>
              </View>
            </View>

            <Text style={styles.sectionTitle}>Feedback</Text>
            <Text>Enjoyed Most: {exit.enjoyedMost}</Text>
            <Text>Enjoyed Least: {exit.enjoyedLeast}</Text>
            <Text>Problems: {exit.problemsEncountered || 'None'}</Text>
            <Text>Benefits Comments: {exit.benefitsComments}</Text>

            <Text style={styles.sectionTitle}>Final Summary</Text>
            <Text>Would Recommend Employer: {exit.recommendEmployer ? 'Yes' : 'No'}</Text>
            <Text>{exit.recommendEmployerExplanation}</Text>
            <Text>Would Reapply: {exit.wouldReapply ? 'Yes' : 'No'}</Text>
            {exit.wouldReapplyExplanation && <Text>{exit.wouldReapplyExplanation}</Text>}
            <Text>Exit Interview Conducted By: {exit.exitInterviewConductedBy}</Text>

            <Text
              style={styles.footer}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
              fixed
            />
          </Page>
        ))}
    </Document>
  )
}

export default ExitFormPdf
